export interface SelectType extends HTMLElement {
  select: SelectField
}

/**
 * Класс для управления кастомным селектом
 */
export class SelectField {
  private static readonly bottomSheetMedia = window.matchMedia(
    '(max-width: 1023px)'
  )

  private container: SelectType
  private trigger: HTMLElement
  private display: HTMLElement
  private optionsContainer: HTMLElement
  private input: HTMLInputElement
  private options: HTMLElement[]
  private bottomSheetBackdrop: HTMLElement | null = null
  private bottomSheetHeader: HTMLElement | null = null
  private optionsParent: Node
  private optionsNextSibling: ChildNode | null
  private isBottomSheet: boolean
  private selectedValue: string = ''
  private opened: boolean = false
  private name: string
  private placeholder: string

  /**
   * Создаёт экземпляр SelectField
   *
   * @param {HTMLElement} container - Контейнер селекта
   */
  constructor(container: SelectType) {
    this.container = container
    this.trigger = container.querySelector('[data-select-trigger]')!
    this.optionsContainer = container.querySelector('[data-select-options]')!
    this.input = container.querySelector('[data-select-input]')!
    this.name = this.input.name
    this.optionsParent = this.optionsContainer.parentNode || container
    this.optionsNextSibling = this.optionsContainer.nextSibling
    this.isBottomSheet = container.hasAttribute('data-bottom-sheet')
    this.options = Array.from(
      container.querySelectorAll('[data-select-option]')
    )
    this.placeholder = this.container.dataset.placeholder || 'Выберите значение'
    this.selectedValue = this.container.dataset.defaultValue || ''
    this.display = this.trigger.querySelector('[data-select-trigger-text]')!
    this.container.select = this

    this.init()
  }

  /**
   * Инициализирует обработчики событий и начальное состояние
   */
  private init(): void {
    // Setup event listeners
    this.trigger.addEventListener('click', () => this.toggle())

    this.options.forEach((option) => {
      option.addEventListener('click', () => {
        const value = option.dataset.value!
        this.selectValue(value)
      })
    })

    this.optionsContainer.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      if (
        this.opened &&
        this.shouldUseBottomSheet() &&
        !target.closest('[data-select-option]')
      ) {
        this.close()
      }
    })

    // Click outside to close
    document.addEventListener('click', (e) => {
      const target = e.target as Node
      if (
        !this.container.contains(target) &&
        !this.optionsContainer.contains(target)
      ) {
        this.close()
      }
    })

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close()
      }
    })

    SelectField.bottomSheetMedia.addEventListener('change', () => this.close())

    // Update UI based on initial value
    if (this.selectedValue) {
      this.updateDisplay()
    }

    const form = this.container.closest('form')
    if (form) {
      form.addEventListener('reset', () => {
        setTimeout(() => {
          this.selectedValue = this.container.dataset.defaultValue || ''
          this.updateDisplay()
        })
      })
    }
  }

  /**
   * Переключает состояние выпадающего списка
   */
  private toggle(): void {
    if (this.opened) {
      this.close()
    } else {
      this.open()
    }
  }

  /**
   * Открывает выпадающий список
   */
  private open(): void {
    this.opened = true
    this.mountBottomSheet()
    this.trigger.classList.add('select-field__trigger_active')
    this.optionsContainer.classList.add(
      'select-field__options-container_visible'
    )
  }

  /**
   * Закрывает выпадающий список
   */
  private close(): void {
    this.opened = false
    this.trigger.classList.remove('select-field__trigger_active')
    this.optionsContainer.classList.remove(
      'select-field__options-container_visible'
    )
    this.unmountBottomSheet()
  }

  /**
   * Выбирает значение и обновляет UI
   *
   * @param {string} value - Значение для выбора
   */
  private selectValue(value: string): void {
    this.selectedValue = value
    this.input.value = value
    this.updateDisplay()
    this.close()
    this.emitChange()
  }

  private shouldUseBottomSheet(): boolean {
    return this.isBottomSheet && SelectField.bottomSheetMedia.matches
  }

  private mountBottomSheet(): void {
    if (!this.shouldUseBottomSheet()) return

    this.optionsContainer.classList.add(
      'select-field__options-container_bottom-sheet'
    )
    this.optionsContainer.classList.toggle(
      'select-field__options-container_dark',
      this.container.dataset.themeDark === 'true'
    )
    this.ensureBottomSheetHeader()

    if (!this.bottomSheetBackdrop) {
      this.bottomSheetBackdrop = document.createElement('button')
      this.bottomSheetBackdrop.type = 'button'
      this.bottomSheetBackdrop.className = 'select-field__bottom-sheet-backdrop'
      this.bottomSheetBackdrop.setAttribute('aria-label', 'Закрыть список')
      this.bottomSheetBackdrop.addEventListener('click', () => this.close())
    }

    document.body.appendChild(this.bottomSheetBackdrop)
    document.body.appendChild(this.optionsContainer)
  }

  private unmountBottomSheet(): void {
    this.bottomSheetBackdrop?.remove()
    this.bottomSheetHeader?.remove()

    this.optionsContainer.classList.remove(
      'select-field__options-container_bottom-sheet',
      'select-field__options-container_dark'
    )

    if (this.optionsContainer.parentNode !== this.optionsParent) {
      this.optionsParent.insertBefore(
        this.optionsContainer,
        this.optionsNextSibling
      )
    }
  }

  private ensureBottomSheetHeader(): void {
    if (!this.bottomSheetHeader) {
      this.bottomSheetHeader = document.createElement('div')
      this.bottomSheetHeader.className = 'select-field__bottom-sheet-header'
      this.bottomSheetHeader.innerHTML = `
        <span class="select-field__bottom-sheet-title"></span>
        <button type="button" class="select-field__bottom-sheet-close" aria-label="Закрыть список">
          <svg width="24" height="24">
            <use href="#close"></use>
          </svg>
        </button>
      `

      this.bottomSheetHeader
        .querySelector<HTMLButtonElement>('.select-field__bottom-sheet-close')
        ?.addEventListener('click', () => this.close())
    }

    const title = this.bottomSheetHeader.querySelector<HTMLElement>(
      '.select-field__bottom-sheet-title'
    )
    if (title) {
      title.textContent =
        this.container.dataset.label || this.placeholder || 'Выберите значение'
    }

    this.optionsContainer.prepend(this.bottomSheetHeader)
  }

  /**
   * Обновляет отображение выбранной опции
   */
  private updateDisplay(): void {
    const selectedOption = this.options.find(
      (opt) => opt.dataset.value === this.selectedValue
    )

    if (selectedOption) {
      const name = selectedOption.dataset.value || ''
      const label = selectedOption.dataset.text || name
      const times = selectedOption.dataset.times || ''

      if (times) {
        this.display.innerHTML = `${label} <span style="opacity: 0.5;">(${times})</span>`
      } else {
        this.display.textContent = label
      }

      this.options.forEach((opt) => {
        opt.classList.toggle(
          'select-field__option_active',
          opt === selectedOption
        )
      })
    } else {
      this.display.textContent = ''
    }
  }

  /**
   * Генерирует событие изменения значения
   */
  private emitChange(): void {
    const event = new CustomEvent('selectfield:change', {
      detail: {
        name: this.name,
        value: this.selectedValue
      },
      bubbles: true
    })
    this.container.dispatchEvent(event)
  }

  /**
   * Возвращает текущее выбранное значение
   *
   * @return {string} Выбранное значение
   */
  public getValue(): string {
    return this.selectedValue
  }

  /**
   * Устанавливает значение селекта
   *
   * @param {string} value - Новое значение
   */
  public setValue(value: string): void {
    this.selectedValue = value
    this.input.value = value
    this.updateDisplay()
  }

  public setOptions(
    options: { value: string; label: string; times?: string }[]
  ): void {
    const wasOpened = this.opened

    if (wasOpened) {
      this.close()
    }

    this.optionsContainer.innerHTML = ''
    this.bottomSheetHeader = null

    this.options = options.map((opt) => {
      const optionText = opt.label
      const optionEl = document.createElement('button')
      optionEl.type = 'button'
      optionEl.classList.add('select-field__option')
      optionEl.dataset.selectOption = ''
      optionEl.dataset.value = opt.value
      optionEl.dataset.text = optionText
      if (opt.times) {
        optionEl.dataset.times = opt.times
        optionEl.innerHTML = `${optionText} <span style="opacity: 0.5;">(${opt.times})</span>`
      } else {
        optionEl.textContent = optionText
      }
      this.optionsContainer.appendChild(optionEl)

      optionEl.addEventListener('click', () => {
        this.selectValue(opt.value)
      })

      return optionEl
    })

    const hasSelectedValue = this.options.some(
      (opt) => opt.dataset.value === this.selectedValue
    )

    if (!hasSelectedValue) {
      this.selectedValue = this.options[0]?.dataset.value || ''
    }

    this.input.value = this.selectedValue
    this.updateDisplay()
  }
}
